import React from "react";
import PropTypes from "prop-types";

// local component imports
import GridCell from "./GridCell";

/**
 * ThreeGridBento renders a fixed 3-cell bento layout via CSS grid:
 *
 *  Mobile                          Desktop
 *  ┌──────────────────────────┐    ┌──────────────┬──────────────┐
 *  │        Cell 0  (3:2)     │    │              │ Cell 1 (16:5)│
 *  ├─────────────┬────────────┤    │ Cell 0 (8:5) ├──────────────┤
 *  │ Cell 1 (3:2)│ Cell 2 (3:2)    │              │ Cell 2 (16:5)│
 *  └─────────────┴────────────┘    └──────────────┴──────────────┘
 *
 * @param {object}   props.cells            - Array of exactly 3 cell data objects.
 * @param {object}   props.gridConfig       - Grid config (columnGap, rowGap, borderRadius).
 * @param {object}   props.videoRefs        - Ref map to video DOM elements.
 * @param {Function} props.onToggleVideoPlay - Callback to toggle video play state.
 * @param {object}   props.gridTextStyle    - Text style settings for grid cells.
 * @param {object}   props.gridBorder       - Border config (borderThickness, borderColor, borderRadius).
 * @param {boolean}  props.isMobile         - Whether the current viewport is mobile.
 * @param {boolean}  props.isAboveFold      - Whether this grid is expected above the fold.
 * @param {boolean}  props.isLcpCandidate   - Whether this grid may contain the LCP image.
 */
const ThreeGridBento = ({
  cells,
  gridConfig = {},
  videoRefs,
  onToggleVideoPlay,
  gridTextStyle = {},
  gridBorder = {},
  isMobile,
  isAboveFold,
  isLcpCandidate,
}) => {
  const gap = gridConfig?.columnGap ?? gridConfig?.rowGap ?? 8;
  const borderThickness = gridBorder?.borderThickness ?? 0;
  const borderColor = gridBorder?.borderColor || "transparent";
  const borderStyle =
    borderThickness > 0 ? `${borderThickness}px solid ${borderColor}` : isMobile ? undefined : "2px solid transparent";
  const borderRadius = gridBorder?.borderRadius ?? gridConfig?.borderRadius ?? 0;

  const [leftCell, rightTopCell, rightBottomCell] = [cells?.[0] ?? {}, cells?.[1] ?? {}, cells?.[2] ?? {}];

  /**
   * Builds the prop object for a GridCell.
   * @param {object} cell - Cell data object.
   * @param {number} index - Cell index in the grid.
   * @param {number} colCount - Number of columns in the grid.
   * @param {string} ratio - CSS aspect-ratio value (e.g. "3/2").
   * @returns {object} Props to spread onto GridCell.
   */
  const sharedProps = (cell, index, colCount, ratio) => ({
    cell,
    index,
    onVideoToggle: onToggleVideoPlay,
    videoRefs,
    gridTextStyle,
    columnCount: colCount,
    isMobile,
    aspectRatio: ratio,
    cellBorderRadius: cell.borderRadius ?? borderRadius,
    cellBorderStyle: borderStyle,
    cellBorderThickness: borderThickness,
    isEager: isAboveFold,
    isPriority: isLcpCandidate,
  });

  return (
    <div
      className="grid w-full grid-cols-2"
      style={{ gap }}
      id={`dynamic_grid-three_grid_bento-${leftCell.id || "cell0"}`}
    >
      {/* Cell 0: full row on mobile (col-span-2), left col spanning 2 rows on desktop (row-span-2) */}
      <div className="col-span-2 md:col-span-1 md:row-span-2">
        <GridCell {...sharedProps(leftCell, 0, isMobile ? 1 : 2, isMobile ? "3/2" : "8/5")} />
      </div>

      {/* Cell 1 & 2: half-width 3:2 on mobile, right col 3.2:1 on desktop */}
      <div>
        <GridCell {...sharedProps(rightTopCell, 1, 2, isMobile ? "3/2" : "16/5")} />
      </div>
      <div>
        <GridCell {...sharedProps(rightBottomCell, 2, 2, isMobile ? "3/2" : "16/5")} />
      </div>
    </div>
  );
};

ThreeGridBento.propTypes = {
  cells: PropTypes.arrayOf(PropTypes.object).isRequired,
  gridConfig: PropTypes.object,
  videoRefs: PropTypes.object.isRequired,
  onToggleVideoPlay: PropTypes.func.isRequired,
  gridTextStyle: PropTypes.object,
  gridBorder: PropTypes.shape({
    borderRadius: PropTypes.number,
    borderThickness: PropTypes.number,
    borderColor: PropTypes.string,
  }),
  isMobile: PropTypes.bool.isRequired,
  isAboveFold: PropTypes.bool,
  isLcpCandidate: PropTypes.bool,
};

export default ThreeGridBento;
